import { CAMERA_SIZE } from '@/common/components/camera/data/camera.data';
import { useCameraProp } from '@/common/components/camera/types/hooksTypes';
import useModal from '@/common/hooks/useModal';
import useToast from '@/common/store/alert/useToast';
import { useEffect, useRef, useState } from 'react';

const useCamera = ({
  audio = true,
  onDenyCamera,
  closeCamera,
}: useCameraProp) => {
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [disableCamera, setDisableCamera] = useState(true);
  const [cameraStatus, setCameraStatus] = useState<
    'ready' | 'loading' | 'idle'
  >('loading');
  const [torchIsOn, setTorchIsOn] = useState(false);
  const rotateModal = useModal({ initialOpen: true });
  const handleRotateModal = useModal();
  const isFirstRender = useRef(true);
  const showAlert = useToast((state) => state.showAlert);

  const getStream = async () => {
    try {
      if (streamRef.current) {
        return streamRef.current;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio,
        video: {
          width: CAMERA_SIZE.width,
          height: CAMERA_SIZE.height,
          facingMode: 'environment',
        },
      });
      streamRef.current = stream;
      setDisableCamera(false);
      return stream;
    } catch (error) {
      onDenyCamera();

      return null;
    }
  };

  const getCameraAccess = async () => {
    if (videoRef.current) {
      let timeOut = setTimeout(() => {
        if (!videoRef.current?.srcObject) {
          setCameraStatus('idle');
          closeCamera();
          showAlert({
            description: 'خطا در دریافت دسترسی . لطفا مجدد امتحان کنید',
            type: 'error',
          });
        }
        clearTimeout(timeOut);
      }, 10000);
      const stream = await getStream();

      if (stream) {
        clearTimeout(timeOut);
        videoRef.current.srcObject = stream;
        videoRef.current.width = CAMERA_SIZE.width;
        videoRef.current.height = CAMERA_SIZE.height;
        setCameraStatus('ready');
      }
      return stream;
    }
  };

  const stopStream = () => {
    const stream = streamRef.current;

    if (stream instanceof MediaStream) {
      const tracks = stream.getTracks();
      setDisableCamera(true);

      tracks.forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop();
        }
      });
    }
  };
  // DUMMY COMMIT
  const handleClickLight = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    if (!track || !('getCapabilities' in track)) return;

    const capabilities = track.getCapabilities();
    if ('torch' in capabilities) {
      track.applyConstraints({
        advanced: [{ torch: !torchIsOn } as any],
      });
    }
    setTorchIsOn((prev) => {
      return !prev;
    });
  };

  useEffect(() => {
    let hasStarted = false;

    const handleResize = () => {
      if (!isFirstRender.current) {
        return;
      } else {
        isFirstRender.current = false;
      }

      if (window.innerWidth > window.innerHeight) {
        rotationIsLandScape();
      } else {
        rotationIsPortrait({ hasStarted });
      }
    };

    window.addEventListener('resize', rotationIsLandScape);
    handleResize();

    return () => {
      window.removeEventListener('resize', rotationIsLandScape);
      stopStream();
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  const rotationIsPortrait = ({ hasStarted }: { hasStarted: boolean }) => {
    if (videoRef.current && !hasStarted) {
      hasStarted = true;
      getCameraAccess();
    }
  };

  const rotationIsLandScape = () => {
    setCameraStatus('idle');

    stopStream();
    handleRotateModal.openModal();
  };

  return {
    stopStream,
    getCameraAccess,
    videoRef,
    disableCamera,
    handleClickLight,
    torchIsOn,
    stream: streamRef,
    setDisableCamera,
    rotateModal,
    handleRotateModal,
    cameraStatus,
  };
};

export default useCamera;
